import React, { useState } from 'react';
import { Badge, Card, Empty, Space, Tooltip, Typography } from 'antd';
import { FormattedMessage } from 'react-intl';
import {
    BellOutlined,
    CheckSquareOutlined,
    LinkOutlined,
    MailOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { ICrmCompany, ICrmPipelineStage } from '../../../services/nestjs-base-api/types';
import { HealthDot, LifecycleTag, SourceIcon, daysSince, formatAest, isPast } from './crmUi';

const { Text } = Typography;

interface CrmKanbanProps {
    stages: ICrmPipelineStage[];
    companies: ICrmCompany[];
    onCardClick: (company: ICrmCompany) => void;
    onStageChange: (company: ICrmCompany, stageId: number) => void;
}

export const CrmKanban: React.FC<CrmKanbanProps> = ({
    stages,
    companies,
    onCardClick,
    onStageChange,
}) => {
    const [dragCompanyId, setDragCompanyId] = useState<number | null>(null);
    const [dragOverStageId, setDragOverStageId] = useState<number | null>(null);

    const activeStages = [...stages]
        .filter((s) => s.isActive)
        .sort((a, b) => a.displayOrder - b.displayOrder);

    const handleDrop = (stageId: number) => {
        const company = companies.find((c) => c.id === dragCompanyId);
        setDragCompanyId(null);
        setDragOverStageId(null);
        if (company && company.pipelineStage.id !== stageId) {
            onStageChange(company, stageId);
        }
    };

    return (
        // The board is the scroll container, sized to the viewport: columns
        // scroll vertically inside themselves, so the horizontal scrollbar is
        // always on screen and the page never grows past one viewport.
        <div
            style={{
                display: 'flex',
                gap: 12,
                overflowX: 'auto',
                paddingBottom: 8,
                alignItems: 'stretch',
                height: 'calc(100vh - 238px)',
                minHeight: 420,
            }}
        >
            {activeStages.map((stage) => {
                const stageCompanies = companies.filter(
                    (c) => c.pipelineStage.id === stage.id,
                );
                return (
                    <div
                        key={stage.id}
                        onDragOver={(e) => {
                            e.preventDefault();
                            setDragOverStageId(stage.id);
                        }}
                        onDragLeave={() => setDragOverStageId(null)}
                        onDrop={() => handleDrop(stage.id)}
                        style={{
                            minWidth: 250,
                            width: 250,
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            background: dragOverStageId === stage.id ? '#f0f5ff' : '#fafafa',
                            border: `1px solid ${dragOverStageId === stage.id ? '#adc6ff' : '#f0f0f0'}`,
                            borderRadius: 8,
                            padding: 8,
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '4px 8px 10px',
                            }}
                        >
                            <Space size={6}>
                                <span
                                    style={{
                                        display: 'inline-block',
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        background: stage.color,
                                    }}
                                />
                                <Text strong>{stage.label}</Text>
                            </Space>
                            <Badge
                                count={stageCompanies.length}
                                showZero
                                color={stageCompanies.length > 0 ? '#18216d' : '#d9d9d9'}
                            />
                        </div>

                        {stageCompanies.length === 0 && (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description=""
                                style={{ margin: '8px 0' }}
                            />
                        )}

                        {/* Cards scroll vertically inside the column; the header
                            stays pinned. overflowX hidden — the vertical
                            scrollbar's width must not create horizontal overflow. */}
                        <Space
                            direction="vertical"
                            size={8}
                            style={{
                                width: '100%',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                flex: 1,
                            }}
                        >
                            {stageCompanies.map((company) => (
                                <Card
                                    key={company.id}
                                    size="small"
                                    hoverable
                                    draggable
                                    onDragStart={() => setDragCompanyId(company.id)}
                                    onDragEnd={() => {
                                        setDragCompanyId(null);
                                        setDragOverStageId(null);
                                    }}
                                    onClick={() => onCardClick(company)}
                                    styles={{ body: { padding: 10 } }}
                                    style={{
                                        opacity: dragCompanyId === company.id ? 0.4 : 1,
                                        cursor: 'grab',
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                                        {/* Flexible truncation (no fixed widths) so cards
                                            never overflow the column, scrollbar or not. */}
                                        <Text strong ellipsis style={{ flex: 1, minWidth: 0 }}>
                                            {company.name}
                                        </Text>
                                        <SourceIcon source={company.source} />
                                    </div>

                                    {company.primaryContact?.name && (
                                        <div style={{ marginTop: 2 }}>
                                            <Text type="secondary" ellipsis style={{ fontSize: 12, display: 'block' }}>
                                                {company.primaryContact.name}
                                                {company.primaryContact.position
                                                    ? ` · ${company.primaryContact.position}`
                                                    : ''}
                                            </Text>
                                        </div>
                                    )}
                                    {company.primaryContact?.email && (
                                        <div>
                                            <Text type="secondary" ellipsis style={{ fontSize: 12, display: 'block' }}>
                                                <MailOutlined style={{ marginRight: 4 }} />
                                                {company.primaryContact.email}
                                            </Text>
                                        </div>
                                    )}

                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            marginTop: 8,
                                            gap: 6,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Space size={4}>
                                            <LifecycleTag stage={company.lifecycleStage} />
                                            {company.organisationId && (
                                                <Tooltip
                                                    title={
                                                        <FormattedMessage
                                                            id="crm.linkedTooltip"
                                                            defaultMessage="Linked to {org}"
                                                            values={{ org: company.organisationName }}
                                                        />
                                                    }
                                                >
                                                    <LinkOutlined style={{ color: '#52c41a' }} />
                                                </Tooltip>
                                            )}
                                            {company.removedFromSheet && (
                                                <Tooltip
                                                    title={
                                                        <FormattedMessage
                                                            id="crm.removedFromSheet"
                                                            defaultMessage="Row removed from sheet — review"
                                                        />
                                                    }
                                                >
                                                    <WarningOutlined style={{ color: '#faad14' }} />
                                                </Tooltip>
                                            )}
                                        </Space>
                                        <Space size={6}>
                                            <HealthDot score={company.healthScore} />
                                            {company.openReminderCount > 0 && company.nextReminderDueAt && (
                                                <Tooltip
                                                    title={
                                                        <FormattedMessage
                                                            id="crm.kanban.reminderTooltip"
                                                            defaultMessage="{count, plural, one {# reminder} other {# reminders}} · next due {when} AEST"
                                                            values={{
                                                                count: company.openReminderCount,
                                                                when: formatAest(company.nextReminderDueAt),
                                                            }}
                                                        />
                                                    }
                                                >
                                                    <BellOutlined
                                                        style={{
                                                            fontSize: 12,
                                                            color: isPast(company.nextReminderDueAt)
                                                                ? '#ff4d4f'
                                                                : '#8c8c8c',
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                            {company.openTaskCount > 0 && (
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    <CheckSquareOutlined style={{ marginRight: 2 }} />
                                                    {company.openTaskCount}
                                                </Text>
                                            )}
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                <FormattedMessage
                                                    id="crm.kanban.daysShort"
                                                    defaultMessage="{days}d"
                                                    values={{ days: daysSince(company.stageEnteredAt) }}
                                                />
                                            </Text>
                                        </Space>
                                    </div>
                                </Card>
                            ))}
                        </Space>
                    </div>
                );
            })}
        </div>
    );
};
